/**
 * Le bruiteur : de la synthèse, pas des fichiers.
 *
 * ## Pourquoi rien n'est enregistré
 *
 * Trois raisons, dans cet ordre :
 *
 * 1. **Les licences.** Le § 12 impose d'archiver les droits de tous les assets pour
 *    pouvoir répondre à Apple. Un son synthétisé n'a pas d'ayant droit.
 * 2. **Le poids.** Le budget du premier affichage est déjà tenu de près (§ 7.3) ;
 *    une douzaine de sons corrects pèse plusieurs centaines de kilo-octets, et il
 *    faudrait les charger avant de pouvoir jouer.
 * 3. **La variation.** Un dé qui claque toujours exactement pareil s'entend comme un
 *    échantillon. Ici chaque choc a sa hauteur et sa durée propres, et trois dés qui
 *    tombent ensemble ne sonnent pas comme un seul dé joué trois fois.
 *
 * Ce module ne sait rien du jeu : il reçoit un `Effet` et le joue. Toute la décision
 * — quoi sonner, quand, à quelle force — vit dans `partition.ts`, qui est pur et
 * testé. Ici il n'y a que du Web Audio, qu'aucun test ne peut exercer sans navigateur.
 *
 * ## Les deux pièges du son sur mobile
 *
 * - **iOS ne laisse rien sonner avant un geste.** Le contexte audio naît suspendu et
 *   ne se réveille que dans un gestionnaire d'événement d'entrée. On le crée donc
 *   paresseusement, au premier geste, et on tente `resume()` à chaque son : un
 *   contexte créé au chargement resterait muet toute la partie.
 * - **L'arrière-plan (§ 5.4).** Le son doit se taire quand l'application passe
 *   derrière, sinon iOS coupe la session audio et plus rien ne revient ensuite.
 */

import type { Effet, NomDeSon } from "./partition.js";

export interface Bruiteur {
  /** Joue un effet. Sans effet si le son est coupé ou indisponible. */
  readonly jouer: (effet: Effet) => void;
  /** À appeler depuis un geste utilisateur : c'est ce qui débloque iOS. */
  readonly reveiller: () => void;
  /** À appeler quand l'application passe en arrière-plan. */
  readonly suspendre: () => void;
  /** 0 coupe le son, 1 est le volume nominal. */
  readonly reglerVolume: (volume: number) => void;
  readonly fermer: () => void;
}

/** Un bruiteur qui ne fait rien — tests, rendu serveur, navigateur sans Web Audio. */
export function bruiteurMuet(): Bruiteur {
  return {
    jouer: () => undefined,
    reveiller: () => undefined,
    suspendre: () => undefined,
    reglerVolume: () => undefined,
    fermer: () => undefined,
  };
}

type Fabrique = new () => AudioContext;

function fabriqueAudio(): Fabrique | null {
  const w = globalThis as unknown as {
    AudioContext?: Fabrique; webkitAudioContext?: Fabrique;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export function creerBruiteur(): Bruiteur {
  const Fabrique = fabriqueAudio();
  if (!Fabrique) return bruiteurMuet();

  let contexte: AudioContext | null = null;
  let maitre: GainNode | null = null;
  let bruitBlanc: AudioBuffer | null = null;
  let volume = 1;
  /**
   * Les voix en cours. Trois dés qui tombent ensemble, chacun claquant huit fois,
   * font vingt-quatre sons en deux secondes : sans plafond, ils s'additionnent et
   * saturent. Le compresseur rattrape le reste.
   */
  let voix = 0;
  const VOIX_MAX = 12;

  const monter = (): AudioContext | null => {
    if (contexte) return contexte;
    try {
      contexte = new Fabrique();
    } catch {
      return null;
    }
    // Un compresseur en sortie : c'est ce qui permet de laisser plusieurs chocs se
    // superposer sans que l'ensemble crache.
    const compresseur = contexte.createDynamicsCompressor();
    compresseur.threshold.value = -18;
    compresseur.ratio.value = 8;
    compresseur.attack.value = 0.003;
    compresseur.release.value = 0.12;

    maitre = contexte.createGain();
    maitre.gain.value = volume * 0.5;
    maitre.connect(compresseur);
    compresseur.connect(contexte.destination);

    // Une seconde de bruit blanc, réutilisée par tous les souffles. L'allouer à
    // chaque choc ferait des kilo-octets de déchets soixante fois par seconde.
    const taille = Math.floor(contexte.sampleRate);
    bruitBlanc = contexte.createBuffer(1, taille, contexte.sampleRate);
    const donnees = bruitBlanc.getChannelData(0);
    for (let i = 0; i < taille; i++) donnees[i] = Math.random() * 2 - 1;

    return contexte;
  };

  const reveiller = () => {
    const ctx = monter();
    if (ctx && ctx.state === "suspended") void ctx.resume().catch(() => undefined);
  };

  /** Souffle filtré : tout ce qui claque, frotte ou siffle. */
  const souffle = (
    ctx: AudioContext, debut: number, duree: number,
    frequence: number, q: number, gain: number,
    type: BiquadFilterType = "bandpass", glissando?: number,
  ) => {
    if (!bruitBlanc || !maitre) return;
    const source = ctx.createBufferSource();
    source.buffer = bruitBlanc;
    source.loop = true;
    source.playbackRate.value = 0.8 + Math.random() * 0.4;

    const filtre = ctx.createBiquadFilter();
    filtre.type = type;
    filtre.frequency.setValueAtTime(frequence, debut);
    if (glissando !== undefined) {
      filtre.frequency.exponentialRampToValueAtTime(
        Math.max(60, glissando), debut + duree);
    }
    filtre.Q.value = q;

    const enveloppe = ctx.createGain();
    enveloppe.gain.setValueAtTime(0.0001, debut);
    enveloppe.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), debut + 0.004);
    enveloppe.gain.exponentialRampToValueAtTime(0.0001, debut + duree);

    source.connect(filtre);
    filtre.connect(enveloppe);
    enveloppe.connect(maitre);
    source.start(debut);
    source.stop(debut + duree + 0.02);
    compter(source);
  };

  /** Un partiel : tout ce qui a une hauteur. */
  const ton = (
    ctx: AudioContext, debut: number, duree: number,
    frequence: number, gain: number,
    type: OscillatorType = "sine", vers?: number,
  ) => {
    if (!maitre) return;
    const oscillateur = ctx.createOscillator();
    oscillateur.type = type;
    oscillateur.frequency.setValueAtTime(frequence, debut);
    if (vers !== undefined) {
      oscillateur.frequency.exponentialRampToValueAtTime(
        Math.max(20, vers), debut + duree);
    }

    const enveloppe = ctx.createGain();
    enveloppe.gain.setValueAtTime(0.0001, debut);
    enveloppe.gain.exponentialRampToValueAtTime(Math.max(0.0002, gain), debut + 0.008);
    enveloppe.gain.exponentialRampToValueAtTime(0.0001, debut + duree);

    oscillateur.connect(enveloppe);
    enveloppe.connect(maitre);
    oscillateur.start(debut);
    oscillateur.stop(debut + duree + 0.02);
    compter(oscillateur);
  };

  const compter = (noeud: AudioScheduledSourceNode) => {
    voix++;
    noeud.onended = () => { voix = Math.max(0, voix - 1); };
  };

  /**
   * Le timbre de chaque son.
   *
   * Le dé est le seul qui compte vraiment : c'est le bruit du jeu. Un claquement de
   * bois sur feutre, c'est une attaque très courte, une résonance basse, et surtout
   * une hauteur qui change à chaque fois — sans cette variation, dix chocs d'affilée
   * s'entendent comme un échantillon rejoué dix fois.
   */
  const timbre = (ctx: AudioContext, son: NomDeSon, force: number) => {
    const t = ctx.currentTime + 0.001;
    const f = Math.min(1, Math.max(0, force));

    switch (son) {
      case "choc": {
        const hauteur = 1400 + Math.random() * 1400;
        souffle(ctx, t, 0.05 + 0.03 * f, hauteur, 1.4, 0.5 * f);
        ton(ctx, t, 0.07, 140 + Math.random() * 60, 0.22 * f, "triangle", 90);
        break;
      }
      case "lancer":
        // Le geste : un souffle qui descend, comme une main qui balance les dés.
        souffle(ctx, t, 0.26, 2600, 0.7, 0.16 * f, "lowpass", 400);
        break;
      case "garde":
        ton(ctx, t, 0.05, 980, 0.28 * f, "sine", 900);
        break;
      case "relache":
        ton(ctx, t, 0.06, 560, 0.24 * f, "sine", 480);
        break;
      case "jeton":
        // Deux partiels aigus très courts : le tintement d'un jeton sur le zinc.
        ton(ctx, t, 0.11, 2350, 0.16 * f);
        ton(ctx, t + 0.01, 0.09, 3320, 0.1 * f);
        souffle(ctx, t, 0.05, 5200, 2, 0.09 * f);
        break;
      case "sec":
        // La claque : une gifle sur le comptoir, plus un coup sourd.
        souffle(ctx, t, 0.13, 1100, 0.9, 0.55 * f, "lowpass", 260);
        ton(ctx, t, 0.18, 96, 0.4 * f, "sine", 58);
        break;
      case "rampeau":
        // Le duel : la salle baisse, un grondement descend.
        ton(ctx, t, 0.55, 165, 0.22 * f, "sawtooth", 88);
        souffle(ctx, t, 0.5, 700, 0.8, 0.12 * f, "lowpass", 180);
        break;
      case "cloche":
        // 421. Le § 3.3 demande une cloche : trois partiels inharmoniques, longue
        // traîne. C'est le seul son du jeu qui a le droit de durer.
        ton(ctx, t, 1.7, 880, 0.3 * f);
        ton(ctx, t + 0.005, 1.3, 1320, 0.17 * f);
        ton(ctx, t + 0.01, 0.9, 2378, 0.09 * f);
        ton(ctx, t, 0.35, 440, 0.12 * f, "triangle");
        break;
      case "grincement":
        // La nénette : « son grinçant » (§ 3.3). Une dent qui racle.
        ton(ctx, t, 0.32, 232, 0.16 * f, "sawtooth", 196);
        souffle(ctx, t, 0.3, 1500, 6, 0.16 * f);
        break;
      case "bascule":
        // La décharge : le sol se dérobe, tout s'inverse.
        ton(ctx, t, 0.85, 132, 0.24 * f, "sine", 66);
        ton(ctx, t + 0.02, 0.8, 131, 0.16 * f, "triangle", 65);
        souffle(ctx, t, 0.7, 900, 0.6, 0.1 * f, "lowpass", 200);
        break;
      case "liberation":
        // Sorti d'affaire : deux notes qui montent, et c'est tout.
        ton(ctx, t, 0.16, 660, 0.18 * f);
        ton(ctx, t + 0.12, 0.28, 990, 0.16 * f);
        break;
      case "fin":
        ton(ctx, t, 0.3, 440, 0.24 * f);
        ton(ctx, t + 0.22, 0.7, 294, 0.22 * f);
        ton(ctx, t + 0.22, 0.7, 220, 0.14 * f, "triangle");
        break;
    }
  };

  return {
    jouer: (effet: Effet) => {
      if (volume <= 0) return;
      const ctx = monter();
      if (!ctx || !maitre) return;
      if (ctx.state === "suspended") {
        // Pas encore débloqué : on tente, et on abandonne ce son-là. Le suivant
        // passera. Empiler des sons en attente ferait sonner tout le début de la
        // partie d'un coup au premier geste.
        void ctx.resume().catch(() => undefined);
        return;
      }
      if (voix >= VOIX_MAX) return;
      try {
        timbre(ctx, effet.son, effet.force);
      } catch {
        // Un contexte fermé par l'OS ne doit pas casser la partie.
      }
    },
    reveiller,
    suspendre: () => {
      if (contexte && contexte.state === "running") {
        void contexte.suspend().catch(() => undefined);
      }
    },
    reglerVolume: (nouveau: number) => {
      volume = Math.min(1, Math.max(0, nouveau));
      if (maitre && contexte) {
        maitre.gain.setTargetAtTime(volume * 0.5, contexte.currentTime, 0.02);
      }
    },
    fermer: () => {
      if (contexte) void contexte.close().catch(() => undefined);
      contexte = null;
      maitre = null;
      bruitBlanc = null;
    },
  };
}
